import {
  showSuccess,
  showConfirm,
  showCustom,
} from "@/assets/js/utils/alertService"; // ✅ 引入共用 SweetAlert2 服務
import api from "@/assets/js/utils/api.js"; // ✅ 引入共用 API 服務
import { DateHelper } from "@/assets/js/utils/time"; // ✅ 引入時間工具

export default {
  name: "CustomerManagement",

  data() {
    return {
      // 客戶列表
      customers: [],

      // 待審核獸醫師
      pendingVets: [],

      // 可用的優惠券列表
      availableCoupons: [],
      orderHistory: [],
      showOrderHistoryModal: false,

      // 身分類別定義
      identityTypes: {
        all: { label: "全部會員", value: "all" },
        Note: { label: "僅客戶建檔", color: "#6b7280" },
        Doctor: { label: "動物醫師", color: "#10b981" },
        Supplier: { label: "廠商(經銷商)", color: "#f59e0b" },
        DrugStore: { label: "廠商(藥局)", color: "#8b5cf6" },
        User: { label: "一般會員", color: "#3b82f6" },
      },

      // 搜尋與篩選
      searchQuery: "",
      activeFilter: "all",

      // Modal 控制
      showAddModal: false,
      showEditModal: false,
      showDetailModal: false,
      showVetReviewModal: false,
      showPreferencesModal: false,
      showCouponModal: false,
      showPendingVetsModal: false,

      // 選中的項目
      selectedCustomer: null,
      selectedVet: null,

      // 表單資料
      formData: {
        identity: "fileonly",
        name: "",
        phone: "",
        address: "",
        email: "",
        companyName: "",
        jobTitle: "",
        companyAddress: "",
        taxId: "",
      },

      // 優惠券派發表單
      couponForm: {
        distributionType: "all", // 'all' 或 'specific'
        selectedCoupon: "",
        selectedMembers: [],
        selectedRole: "", // 👈 新增這個欄位
      },

      // ✅ 分頁相關
      currentPage: 1, // 當前頁數
      itemsPerPage: 20, // 每頁顯示 20 筆
    };
  },

  computed: {
    // 篩選後客戶
    filteredCustomers() {
      return this.customers.filter((customer) => {
        const matchFilter =
          this.activeFilter === "all" ||
          customer.identity === this.activeFilter;

        // 🔍 搜尋全部屬性
        const query = this.searchQuery.toLowerCase();
        const matchSearch =
          this.searchQuery === "" ||
          Object.values(customer).some((val) =>
            String(val).toLowerCase().includes(query),
          );

        return matchFilter && matchSearch;
      });
    },

    // ✅ 分頁後的客戶列表
    paginatedCustomers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCustomers.slice(start, end);
    },

    // ✅ 總頁數
    totalPages() {
      return Math.ceil(this.filteredCustomers.length / this.itemsPerPage);
    },

    // 符合派發資格者
    eligibleMembers() {
      return this.customers; // ✅ 不再過濾掉任何人
    },
  },

  // ✅ 監視搜尋和篩選，重置頁碼
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    activeFilter() {
      this.currentPage = 1;
    },
  },

  async mounted() {
    await this.fetchCustomers();
    await this.fetchPendingVets();
    await this.fetchCoupons();
  },

  methods: {
    async fetchPendingVets() {
      try {
        const { data: res } = await api.get("/doctor/review/list");

        if (res.success && Array.isArray(res.data)) {
          this.pendingVets = res.data.map((v) => ({
            id: v.Id,
            doctorName: v.FullName,
            hospitalName: v.CompanyName,
            phone: v.PhoneNumber,
            email: v.Email,
            jobTitle: v.JobTitle,
            hospitalAddress: v.CompanyAddress,
            vetCertificate: v.Picture || require("@/assets/image/AYH.png"),
            submittedAt: DateHelper.toTaiwanFormat(v.DoctorCheckDate),
            taxId: v.TaxId,
            status: "pending",
          }));
        }
      } catch (err) {
        showCustom({
          icon: "error",
          title: "取得待審核獸醫師失敗",
          text: err.response?.data?.message || err.message,
        });
      }
    },

    async reviewDoctor(id, isApproved) {
      try {
        const payload = {
          Id: id,
          IsApproved: isApproved,
        };

        const { data: res } = await api.post("/doctor/review/approve", payload);

        if (res.success) {
          showSuccess(res.message || "審核完成！");
          await this.fetchCustomers(); // ✅ 更新客戶列表
          await this.fetchPendingVets(); // ✅ 更新待審核
        } else {
          showCustom({
            icon: "warning",
            title: "審核失敗",
            text: res.message,
          });
        }
      } catch (e) {
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: e.response?.data?.message || e.message,
        });
      }
    },

    async fetchCustomerTopProducts(customerId) {
      try {
        const { data: res } = await api.get(
          `/customer/${customerId}/top-products`,
        );

        if (res.success && Array.isArray(res.data)) {
          return res.data.map((p) => ({
            id: p.ProductId, // 商品 ID   ← 後端回傳
            name: p.ProductName, // 商品名稱
            count: p.OrderCount, // 購買次數
            quantity: p.TotalQuantity, // 總數量
            amount: 0, // 若要金額可另外串
            unit: "件", // 可改成你商品的單位
          }));
        }

        return [];
      } catch (error) {
        showCustom({
          icon: "error",
          title: "常購商品資料讀取失敗",
          text: error.response?.data?.message || error.message,
        });
        return [];
      }
    },
    async fetchCoupons() {
      try {
        const { data: res } = await api.get("/coupon");

        if (res.success && Array.isArray(res.data)) {
          const now = new Date();

          this.availableCoupons = res.data
            .filter((c) => new Date(c.EndDate) >= now)
            .map((c) => ({
              id: c.Id, // ✅ 統一改小寫
              CouponName: c.CouponName,
              Description: c.Description,
              CouponType: c.CouponType,
              DiscountValue: c.DiscountValue,
              IsActive: c.IsActive,
            }));
        }
      } catch (err) {
        showCustom({
          icon: "error",
          title: "優惠券載入失敗",
          text: err.response?.data?.message || err.message,
        });
      }
    },
    // 統計人數
    getIdentityCount(identity) {
      if (identity === "all") return this.customers.length;
      return this.customers.filter((c) => c.identity === identity).length;
    },

    async fetchCustomers() {
      try {
        const { data: res } = await api.get("/customer");

        if (res.success && Array.isArray(res.data)) {
          // 先建立 customer 基本資料
          this.customers = res.data.map((c) => ({
            id: c.Id,
            name: c.FullName,
            phone: c.PhoneNumber,
            address: c.Address,
            email: c.Email,
            companyName: c.CompanyName,
            jobTitle: c.JobTitle,
            companyAddress: c.CompanyAddress,
            taxId: c.TaxId,
            createdAt: DateHelper.toTaiwanFormat(c.CreateDate),
            identity: c.Auth,
            favoriteProducts: [], // 常購商品
            noOrder: false, // 是否完全沒有訂單
            isActive: c.IsActive !== false, // 客戶啟用狀態（預設為 true）
          }));

          // 🔥 逐一查詢常購商品
          for (const customer of this.customers) {
            try {
              const { data: tp } = await api.get(
                `/customer/${customer.id}/top-products`,
              );

              // 🟥 沒有訂單紀錄
              if (tp.message === "該用戶沒有訂單紀錄") {
                customer.favoriteProducts = [];
                customer.noOrder = true; // <- 標記：沒有訂單
                continue;
              }

              // 🟩 有資料 → 只取商品名稱
              if (tp.success && Array.isArray(tp.data)) {
                customer.favoriteProducts = tp.data.map((p) => ({
                  id: p.ProductId,
                  name: p.ProductName,
                }));
              }
            } catch (err) {
              console.warn(`查詢客戶 ${customer.id} 常購商品失敗`, err);
              customer.favoriteProducts = [];
              customer.noOrder = true;
            }
          }
        } else {
          showCustom({
            icon: "error",
            title: "資料載入失敗",
            text: res.message || "伺服器未回傳有效資料。",
          });
        }
      } catch (error) {
        if (error.response?.status === 401) return;
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: error.response?.data?.message || error.message,
        });
      }
    },

    formatNumber(num) {
      return num?.toLocaleString() || "0";
    },

    // 📦 新增客戶建檔
    async handleAddCustomer() {
      try {
        const payload = {
          fullName: this.formData.name, // 姓名
          phoneNumber: this.formData.phone, // 電話
          address: this.formData.address, // 地址
          email: this.formData.email, // Email
          companyName: this.formData.companyName, // 公司名稱
          jobTitle: this.formData.jobTitle, // 職稱
          companyAddress: this.formData.companyAddress, // 公司地址
          taxId: this.formData.taxId, // 統編
          Auth: "Note", // 權限驗證（自定義）
        };

        const response = await api.post("/customer/register", payload); // 發送 POST 請求
        const result = response?.data || {}; // 安全解構資料

        // ✅ 成功回應處理
        if (result.success) {
          showSuccess(result.message || "客戶建檔成功！");
          this.showAddModal = false;
          await this.fetchCustomers(); // 重新載入客戶清單
        } else {
          // ⚠️ 後端回傳 success: false 但非例外
          const errorText =
            result.detail || result.message || "建檔失敗，請稍後再試。";

          await showCustom({
            icon: "error",
            title: "建檔失敗",
            text: errorText,
          });
        }
      } catch (error) {
        if (error.response?.status === 401) return; // ⛔ 未授權由全域攔截器處理

        const errData = error.response?.data || {}; // 取得後端回傳錯誤資料

        // 🎯 判斷是否是 Email 重複錯誤（根據後端訊息關鍵字）
        const isDuplicateEmail =
          errData.detail?.toLowerCase().includes("email") &&
          errData.detail?.toLowerCase().includes("exist");

        const errorMessage = isDuplicateEmail
          ? "此 Email 已被註冊，請使用其他 Email。" // 👈 自訂錯誤提示
          : errData.detail ||
            errData.message ||
            error.message ||
            "伺服器發生錯誤，請稍後再試。";

        showCustom({
          icon: "error",
          title: "建檔失敗",
          text: errorMessage,
        });
      }
    },

    // ✅ 編輯客戶（串接後端）
    async handleEditCustomer() {
      try {
        const confirmed = await showConfirm("確定要更新此客戶資料嗎？");
        if (!confirmed.isConfirmed) return;

        const payload = {
          fullName: this.selectedCustomer.name,
          phoneNumber: this.selectedCustomer.phone,
          address: this.selectedCustomer.address,
          email: this.selectedCustomer.email,
          companyName: this.selectedCustomer.companyName,
          jobTitle: this.selectedCustomer.jobTitle,
          companyAddress: this.selectedCustomer.companyAddress,
          taxId: this.selectedCustomer.taxId,
          auth: this.selectedCustomer.identity,
          isActive: true,
        };

        const { data: res } = await api.put(
          `/customer/${this.selectedCustomer.id}`,
          payload,
        );

        if (res.success) {
          showSuccess("顧客資料更新成功！");
          this.showEditModal = false;
          await this.fetchCustomers(); // 重新載入
        } else {
          showCustom({
            icon: "warning",
            title: "更新失敗",
            text: res.message || "伺服器未能更新資料。",
          });
        }
      } catch (error) {
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: error.response?.data?.message || error.message,
        });
      }
    },

    openEditModal(customer) {
      this.selectedCustomer = { ...customer };
      this.showEditModal = true;
    },

    openVetReview(vet) {
      this.selectedVet = vet;
      this.showVetReviewModal = true;
      this.showPendingVetsModal = false;
    },

    // ✅ 審核獸醫
    async handleVetReview(approved) {
      const confirmed = await showConfirm(
        approved ? "確定通過審核嗎？" : "確定拒絕此醫師嗎？",
      );
      if (!confirmed.isConfirmed) return;

      await this.reviewDoctor(this.selectedVet.id, approved); // ✅ 呼叫後端

      this.showVetReviewModal = false;
      this.selectedVet = null;
    },

    // ✅ 刪除客戶
    async deleteCustomer(id) {
      try {
        const confirmed = await showConfirm("確定要刪除此客戶嗎？");
        if (!confirmed.isConfirmed) return;

        // 🔥 呼叫後端刪除 API
        const { data: res } = await api.delete(`/customer/${id}`);

        if (res.success) {
          // ✅ 前端同步移除
          this.customers = this.customers.filter((c) => c.id !== id);
          await this.$nextTick();
          showSuccess(res.message || "客戶已刪除！");
        } else {
          showCustom({
            icon: "warning",
            title: "刪除失敗",
            text: res.message || "伺服器未能成功刪除客戶。",
          });
        }
      } catch (error) {
        if (error.response?.status === 401) return; // JWT 過期交給攔截器處理
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: error.response?.data?.message || error.message,
        });
      }
    },

    // ✅ 切換客戶啟用/停用狀態
    async toggleCustomerStatus(customerId) {
      try {
        const customer = this.customers.find((c) => c.id === customerId);
        if (!customer) return;

        const newStatus = !customer.isActive;
        const statusText = newStatus ? "啟用" : "停用";
        const confirmed = await showConfirm(`確定要${statusText}此客戶嗎？`);
        if (!confirmed.isConfirmed) return;

        // 🔥 呼叫後端更新 API - 傳送完整客戶資料
        const payload = {
          fullName: customer.name,
          phoneNumber: customer.phone,
          address: customer.address,
          email: customer.email,
          companyName: customer.companyName,
          jobTitle: customer.jobTitle,
          companyAddress: customer.companyAddress,
          taxId: customer.taxId,
          auth: customer.identity,
          isActive: newStatus,
        };

        const { data: res } = await api.put(`/customer/${customerId}`, payload);

        if (res.success) {
          customer.isActive = newStatus;
          showSuccess(`客戶已${statusText}！`);
        } else {
          showCustom({
            icon: "warning",
            title: "更新失敗",
            text: res.message || "伺服器未能更新客戶狀態。",
          });
        }
      } catch (error) {
        if (error.response?.status === 401) return;
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: error.response?.data?.message || error.message,
        });
      }
    },

    // ✅ 派發優惠券
    async handleCouponDistribution() {
      try {
        const selectedCoupon = this.availableCoupons.find(
          (c) => c.id === Number(this.couponForm.selectedCoupon),
        );

        if (!selectedCoupon) {
          showCustom({
            icon: "warning",
            title: "請選擇優惠券",
            text: "請先選擇要派發的優惠券。",
          });
          return;
        }

        let payload = null;

        // 🎯 群發給所有會員
        if (this.couponForm.distributionType === "all") {
          const confirmed = await showConfirm(
            `確定要將「${selectedCoupon.CouponName}」派發給所有會員嗎？`,
          );
          if (!confirmed.isConfirmed) return;

          payload = {
            CouponId: Number(this.couponForm.selectedCoupon),
            Mode: "all", // ✅ 必填
          };
        }

        // 🎯 指定身分組
        else if (this.couponForm.distributionType === "byRole") {
          if (!this.couponForm.selectedRole) {
            showCustom({
              icon: "warning",
              title: "請選擇身分組",
              text: "請選擇要派發的會員身分組。",
            });
            return;
          }

          const confirmed = await showConfirm(
            `確定要將「${selectedCoupon.CouponName}」派發給「${
              this.identityTypes[this.couponForm.selectedRole].label
            }」身分組的所有會員嗎？`,
          );
          if (!confirmed.isConfirmed) return;

          payload = {
            CouponId: Number(this.couponForm.selectedCoupon),
            Mode: "role",
            Role: this.couponForm.selectedRole,
          };
        }

        // 🎯 指定會員
        else if (this.couponForm.distributionType === "specific") {
          if (this.couponForm.selectedMembers.length === 0) {
            showCustom({
              icon: "warning",
              title: "未選擇會員",
              text: "請至少選擇一位會員。",
            });
            return;
          }

          const confirmed = await showConfirm(
            `確定要將「${selectedCoupon.CouponName}」派發給選定的 ${this.couponForm.selectedMembers.length} 位會員嗎？`,
          );
          if (!confirmed.isConfirmed) return;

          payload = {
            CouponId: Number(this.couponForm.selectedCoupon),
            Mode: "customer",
            CustomerIds: this.couponForm.selectedMembers,
          };
        }

        // 🚨 防呆：沒選模式也沒建立 payload
        if (!payload) {
          showCustom({
            icon: "error",
            title: "發送失敗",
            text: "派發模式有誤，請重新選擇。",
          });
          return;
        }

        const { data: res } = await api.post("/coupon/distribute", payload);

        if (res.success) {
          showSuccess(res.message || "優惠券派發成功！");
          this.closeCouponModal();
        } else {
          showCustom({
            icon: "warning",
            title: "派發失敗",
            text: res.message || "伺服器未能完成派發。",
          });
        }
      } catch (error) {
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: error.response.data.detail || error.message,
        });
      }
    },

    // ✅ 關閉 Modal
    closeModal() {
      this.showAddModal = false;
      this.showEditModal = false;
      this.showVetReviewModal = false;
      this.formData = {
        identity: "fileonly",
        name: "",
        phone: "",
        address: "",
        email: "",
        companyName: "",
        jobTitle: "",
        companyAddress: "",
        taxId: "",
      };
    },

    closeCouponModal() {
      this.showCouponModal = false;
      this.couponForm = {
        distributionType: "all",
        selectedCoupon: "",
        selectedMembers: [],
      };
    },

    async viewOrderHistory(customerId) {
      try {
        const { data: res } = await api.get(
          `/order/list/customer/${customerId}`,
        );

        if (!res.success) {
          showCustom({
            icon: "error",
            title: "查詢失敗",
            text: res.message,
          });
          return;
        }

        // 確保 orderHistory 是陣列
        this.orderHistory = Array.isArray(res.data) ? res.data : [];
        this.showOrderHistoryModal = true;

        // 如果沒有訂單記錄，可以選擇性地顯示提示（可選）
        if (this.orderHistory.length === 0) {
          console.log("此客戶沒有訂單記錄");
        }
      } catch (err) {
        showCustom({
          icon: "error",
          title: "查詢失敗",
          text: err.response?.data?.message || err.message,
        });
      }
    },
    formatDate(date) {
      return DateHelper.toTaiwanFormat(date); // 使用你共用的時間工具
    },

    goToOrderDetail(orderId) {
      this.$router.push(`/admin/order?id=${orderId}`); // ← 跳轉到訂單頁
    },

    viewCustomerDetail(customer) {
      this.selectedCustomer = customer;
      this.showDetailModal = true;
    },
  },
};
